import { useMutation } from '@apollo/client';
import { pluralize } from 'inflection';
import * as _ from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import FeatureIntegrationPrompt from './FeatureIntegrationPrompt';
import MentionsTab from './MentionsTab';
import * as styles from './MultiFeatureIntegrationModal.module.scss';
import ProfilesTab from './ProfilesTab';
import RepoSyncTab from './RepoSyncTab';
import SCIMTab from './SCIMTab';
import SSOTab from './SSOTab';
import TeamSearchTab from './TeamSearchTab';
import useDeleteIntegration from './useDeleteIntegration';
import useTeamSearchSources from './useTeamSearchSources';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Form, { Submit } from '../../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../../components/Modal';
import Tabs, { Tab } from '../../components/Tabs';
import Tooltip from '../../components/Tooltip';
import ALL_INTEGRATIONS_QUERY from '../../graphql/allIntegrations';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { IntegrationSettingsInput } from '../../graphql/generated/graphql';
import { IntegrationFeatureStatus } from '../../graphql/generated/graphql';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';
import {
  INSTALLABLE_PLATFORM_INFO_MAP,
  MentionMode,
} from '../../integrations/IntegrationPlatformInfo';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import useNavigate from '../../utils/router/useNavigate';

type UnionToIntersection<U> = (
  U extends unknown ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

type AttributesType<ObjectsType> = {
  [K in keyof UnionToIntersection<ObjectsType>]: UnionToIntersection<ObjectsType>[K];
};

const MODAL_WIDTH = 465;

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const UPDATE_INTEGRATION_MUTATION = gql(/** @complexity 10 */ `
  mutation updateIntegration(
    $platform: String!
    $settings: IntegrationSettingsInput!
  ) {
    updateIntegration(platform: $platform, settings: $settings) {
      ...AllIntegrationsIntegration
      ...SSOTab_integration
      ...SCIMTab_integration
      ...ProfilesTab_integration
      ...MentionsTab_integration
      ...TeamSearchTab_integration
      ...RepoSyncTab_integration
    }
  }
`);

const ALL_TABS = [
  {
    feature: 'sso',
    title: 'Authentication',
    getDescription: ({ name }: IntegrationPlatformInfo) =>
      `Allow teammates to sign into Slab using ${name}.`,
    FieldSet: SSOTab,
  } as const,
  {
    feature: 'scim',
    title: 'Provisioning',
    getDescription: ({ name }: IntegrationPlatformInfo) =>
      `Keep your ${name} identities synced with Slab through SCIM`,
    FieldSet: SCIMTab,
    // TODO: Remove this to enable Microsoft SCIM on frontend
    additionalCheck: ({ platform }: { platform: string }) =>
      platform !== 'microsoft',
  } as const,
  {
    feature: 'profiles',
    title: 'Profiles',
    getDescription: ({ name }: IntegrationPlatformInfo) =>
      `Sync user profile fields from ${name}`,
    FieldSet: ProfilesTab,
  } as const,
  {
    feature: 'mention',
    title: 'Mentions',
    getDescription: ({
      mention: { mentionName = 'content', mentionDisplayName } = {},
    }: IntegrationPlatformInfo) =>
      `${_.capitalize(
        mentionDisplayName ?? mentionName,
      )} links can be automatically expanded in Slab posts.`,
    FieldSet: MentionsTab,
    additionalCheck: ({ platform }: { platform: string }) =>
      !!INSTALLABLE_PLATFORM_INFO_MAP[platform]?.mention,
  } as const,
  {
    feature: 'teamSearch',
    title: 'Search',
    getDescription: ({
      platform,
      mention: { mentionName = 'content' } = {},
      name,
    }: IntegrationPlatformInfo) =>
      platform === 'google'
        ? 'Allow teammates to search for Google Drive content within Slab. Teammates will still use their own permissions, so they will not be able to find anything they do not have access to.'
        : `Allow teammates to search for ${name} ${mentionName} within Slab.`,
    FieldSet: TeamSearchTab,
    additionalCheck: ({ platform }: { platform: string }) => {
      const info = INSTALLABLE_PLATFORM_INFO_MAP[platform];
      return !!info?.mention?.searchEnabledOptions;
    },
  } as const,
  {
    feature: 'repoSync',
    title: 'Sync',
    getDescription: () =>
      'Sync markdown documents from selected repos. You will be able to read, search, and organize them like other Slab posts',
    FieldSet: RepoSyncTab,
  } as const,
];

type Feature = (typeof ALL_TABS)[number]['feature'];
type Values = AttributesType<
  ReturnType<(typeof ALL_TABS)[number]['FieldSet']['getFormValues']>
>;

// eslint-disable-next-line slab-graphql/no-unused-fields
const INTEGRATION_FRAGMENT = gql(`
  fragment MultiFeatureIntegrationModal_integration on Integration {
    id
    platform
    features {
      sso
      scim
      profiles
      mention
      teamSearch
      repoSync
    }
    ...useTeamSearchSources_integration
    ...SSOTab_integration
    ...SCIMTab_integration
    ...ProfilesTab_integration
    ...MentionsTab_integration
    ...TeamSearchTab_integration
    ...RepoSyncTab_integration
  }
`);

interface MultiFeatureIntegrationModalProps {
  integration: DocumentType<typeof INTEGRATION_FRAGMENT>;
}

const MultiFeatureIntegrationModal = ({
  integration,
}: MultiFeatureIntegrationModalProps) => {
  const navigate = useNavigate();
  const params = useParams<{ status: string }>();
  const { features, platform } = integration;
  const platformInfo = INSTALLABLE_PLATFORM_INFO_MAP[platform];

  const [deleteIntegration] = useDeleteIntegration();
  const [updateIntegration] = useMutation(UPDATE_INTEGRATION_MUTATION, {
    update: legacyUpdateQueryList(
      { query: ALL_INTEGRATIONS_QUERY },
      'session.organization.integrations',
      'updateIntegration',
    ),
  });

  const dispatch = useAppDispatch();
  const close = () => dispatch(UIActions.closeModal());

  const tabs = platformInfo
    ? ALL_TABS.filter(
        (tab) =>
          features?.[tab.feature] &&
          features[tab.feature] !== IntegrationFeatureStatus.Unsupported &&
          (!tab.additionalCheck || tab.additionalCheck(integration)),
      ).map((tab) => {
        if (tab.feature === 'mention') {
          const modes = platformInfo.mention?.mentionModes || [];
          let title;
          let getDescription;

          if (
            modes.includes(MentionMode.Embed) &&
            modes.includes(MentionMode.Mention)
          ) {
            title = 'Mentions & Embeds';
            getDescription = () =>
              `${platformInfo.name} links can be automatically expanded or have their previews embedded in Slab posts.`;
          } else if (modes.includes(MentionMode.Embed)) {
            title = 'Embeds';
            getDescription = () =>
              `${platformInfo.name} ${pluralize(
                platformInfo.mention?.mentionName ?? '',
              )} can be embedded in Slab posts.`;
          } else {
            title = tab.title;
            getDescription = tab.getDescription;
          }

          return { ...tab, title, getDescription };
        }

        return tab;
      })
    : [];

  const [activeFeature, setActiveFeature] = useState<Feature | null>(() => {
    const initialFeature = _.camelCase(params.status) as Feature;

    if (tabs.length === 0) return null;

    return initialFeature && tabs.find((tab) => tab.feature === initialFeature)
      ? initialFeature
      : (tabs[0]?.feature ?? null);
  });

  useEffect(() => {
    if (activeFeature) {
      navigate(
        `/admin/integrations/${platform}/${_.kebabCase(activeFeature) || ''}`,
        { replace: true },
      );
    }
  }, [navigate, platform, activeFeature]);

  useEffect(
    () => () => {
      navigate('/admin/integrations');
    },
    [navigate],
  );

  const user = useAppSelector((state) => state.session.user);
  const { data: { sources: teamSearchSources } = {} } =
    useTeamSearchSources(integration);

  if (!platformInfo) {
    return null;
  }

  if (tabs.length === 0) {
    return (
      <Modal width={MODAL_WIDTH}>
        <ModalTitle close={close} noMargin>
          {platformInfo?.name} Integration
        </ModalTitle>
        <div className={styles.content}>
          This integration does not have any configurable settings.
        </div>
        <ModalActionFooter
          secondary={
            <Button
              variant="link"
              danger
              onClick={() => deleteIntegration(platform)}
            >
              Remove
            </Button>
          }
          primary={
            <Button variant="primary" size="large" onClick={close}>
              Close
            </Button>
          }
        />
      </Modal>
    );
  }

  const extraData = { user, teamSearchSources };

  const initialValueMap = _.fromPairs(
    tabs.map(({ feature, FieldSet: { getFormValues } }) => [
      feature,
      getFormValues(integration, extraData),
    ]),
  );

  const initialValues: Values = _.extend({}, ...Object.values(initialValueMap));

  const handleSubmit = (values: Values) => {
    const newSettings: IntegrationSettingsInput = _.extend(
      {},
      ...tabs.map(({ FieldSet: { getNewSettings } }) =>
        getNewSettings(values, extraData),
      ),
    );

    return updateIntegration({
      variables: { platform, settings: newSettings },
    }).then(() => {
      close();

      const message =
        features?.repoSync === IntegrationFeatureStatus.Enabled
          ? 'Posts from your repos are now being continuously synced. It may take some time to finish the initial sync.'
          : `Your ${platformInfo.name} integration settings have been successfully saved.`;

      dispatch(
        UIActions.pushToast({
          message,
          level: 'success',
          title: `${platformInfo.name} integration updated`,
        }),
      );
    });
  };

  const description = tabs
    .find((tab) => tab.feature === activeFeature)
    ?.getDescription(platformInfo);

  return (
    <Modal width={MODAL_WIDTH}>
      <ModalTitle close={close} noMargin>
        {platformInfo.name} Integration
      </ModalTitle>
      <Form<Values>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ dirty, values }) => (
          <div className={styles.content}>
            <Tabs>
              {tabs.map(({ feature, title }) => {
                const isTabDirty = Object.entries(
                  initialValueMap[feature] ?? {},
                ).some(
                  ([key, initialValue]) =>
                    // @ts-expect-error
                    !_.isEqual(values[key], initialValue),
                );

                return (
                  <Tab
                    key={feature}
                    active={activeFeature === feature}
                    onClick={() => setActiveFeature(feature)}
                  >
                    {title}
                    {features?.[feature] ===
                      IntegrationFeatureStatus.Disabled && (
                      <div className={styles.statusContainer}>
                        <Badge color="gray">Off</Badge>
                      </div>
                    )}
                    {isTabDirty && (
                      <Tooltip trigger="clickOrHover">
                        <div className={styles.dotContainer}>
                          <span className={styles.dot} />
                        </div>
                      </Tooltip>
                    )}
                  </Tab>
                );
              })}
            </Tabs>
            {activeFeature &&
              features?.[activeFeature] !== IntegrationFeatureStatus.Enabled &&
              platformInfo && (
                <FeatureIntegrationPrompt
                  description={description}
                  feature={activeFeature}
                  integration={integration}
                  platformInfo={platformInfo}
                />
              )}
            <div
              className={
                activeFeature &&
                features?.[activeFeature] !== IntegrationFeatureStatus.Enabled
                  ? styles.hide
                  : undefined
              }
            >
              <div className={styles.description}>{description}</div>
              <div>
                {tabs.map(({ feature, FieldSet }) => {
                  const tabPlatformInfo =
                    INSTALLABLE_PLATFORM_INFO_MAP[platform];
                  return (
                    tabPlatformInfo && (
                      <div
                        key={feature}
                        className={
                          feature !== activeFeature ? styles.hide : undefined
                        }
                      >
                        <FieldSet
                          integration={integration}
                          platformInfo={tabPlatformInfo}
                        />
                      </div>
                    )
                  );
                })}
              </div>
            </div>
            <ModalActionFooter
              secondary={
                <Button
                  variant="link"
                  danger
                  onClick={() => deleteIntegration(platform)}
                >
                  Remove
                </Button>
              }
              primary={
                <Submit disabled={!dirty} size="large">
                  Save
                </Submit>
              }
            />
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default MultiFeatureIntegrationModal;
